import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { UserModel, PlayerModel } from 'src/app/models/user.model';
import { NoticeModel } from 'src/app/models/notice.model';
import { RightsModel } from 'src/app/models/rights.model';

import { environment } from 'src/environments/environment';
import { HttpHeadersHelper } from 'src/app/helpers/headers';

import { AuthService } from 'src/app/services/auth.service';

@Injectable({
  providedIn: 'root'
})
export class UserService {

  protected apiUrl = environment.apiUrl;
  public userInfo:      UserModel = null;
  public playerInfo:    any = null;
  public affiliates:    UserModel[] = [];
  protected rights:     RightsModel[] = [];

  constructor(
    private http: HttpClient,
    public auth: AuthService,
  ) { }

  public async register(userInfo: UserModel): Promise<string | void> {
    // console.log('register(): Entering');
    return new Promise<string | void>((resolve, reject) => {
      this.http.post(this.apiUrl + '/registration', userInfo, {
        headers: HttpHeadersHelper.createHeaders(),
        withCredentials: false
      })
        .toPromise()
        .then()
        .catch((err: HttpErrorResponse) => {
          console.error('register(): ', err.error);
          reject(err.error);
        });
    });
  }

  public async activate(code: string): Promise<string | void> {
    // console.log('activate(): Entering');
    return new Promise<string | void>((resolve, reject) => {
      this.http.post(this.apiUrl + '/activation', {code: code}, {
        headers: HttpHeadersHelper.createHeaders(),
        withCredentials: false
      })
        .toPromise()
        .then((result: { error: string, success: { token: string } }) => {
          console.log('activate(): ', result);
          if (result.error) {
            reject(result.error);
          } else {
            resolve();
          }
        })
        .catch((err: HttpErrorResponse) => {
          console.error('activate(): ', err);
          reject(err);
        });
    });
  }

  public async forgotSendMail(email: string, captchaResponse: string): Promise<string | void> {
    // console.log('forgotSendMail(): Entering');
    return new Promise<string | void>((resolve, reject) => {
      this.http.post(this.apiUrl + '/remember/mail',
      {
        email:            email,
        captchaResponse:  captchaResponse
      }, {
        headers: HttpHeadersHelper.createHeaders(),
        withCredentials: false
      })
        .toPromise()
        .then(() => {
          console.log('forgotSendMail(): Email sent');
          resolve();
        })
        .catch((result: { error: { error: string } }) => {
          console.error('forgotSendMail(): ', result.error.error);
          reject(result.error.error);
        });
    });
  }

  public async forgotChangePassword(data: object): Promise<string | void> {
    // console.log('forgotChangePassword(): Entering');
    return new Promise<string | void>((resolve, reject) => {
      this.http.post(this.apiUrl + '/remember/password', data, {
        headers: HttpHeadersHelper.createHeaders(),
        withCredentials: false
      })
        .toPromise()
        .then(() => {
          console.log('forgotChangePassword(): Password changed');
          resolve();
        })
        .catch((result: { error: { error: string } }) => {
          console.error('forgotChangePassword(): ', result.error.error);
          reject(result.error.error);
        });
    });
  }

  public async updateUser(userInfo: UserModel): Promise<UserModel> {
    // console.log('updateUser(): Entering');
    const token = await this.auth.getToken();

    return new Promise<UserModel>((resolve, reject) => {
      this.http.post(this.apiUrl + '/user', userInfo, {
        headers: HttpHeadersHelper.createHeaders(token),
        withCredentials: false
      })
      .toPromise()
      .then((result: { success: boolean, api_token?: string, api_token_name?: string }) => {
        if (result.success) {
          this.userInfo.timezone  = userInfo.timezone;
          this.userInfo.name      = userInfo.name;
          this.userInfo.surname   = userInfo.surname;
          this.userInfo.phone     = userInfo.phone;
          this.userInfo.city      = userInfo.city;

          if (result.api_token !== undefined) {
            this.userInfo.api_token = result.api_token;
          }

          if (result.api_token_name !== undefined) {
            this.userInfo.api_token_name = result.api_token_name;
          }

          resolve(this.userInfo);
        }
      })
      .catch((err: HttpErrorResponse) => {
        console.error('updateUser(): ', err.error);
        reject(err.error);
      });
    });
  }

  public async updatePassword(passwordData: UserModel): Promise<string | void> {
    // console.log('updatePassword(): Entering: ', passwordData);
    const token = await this.auth.getToken();

    return new Promise<string | void>((resolve, reject) => {
      this.http.post(this.apiUrl + '/password', passwordData, {
        headers: HttpHeadersHelper.createHeaders(token),
        withCredentials: false
      })
      .toPromise()
      .then((result: { success: boolean, token: string }) => {
        if (result.success) {
          this.auth.setToken(result.token);
          resolve();
        }
      })
      .catch((err: HttpErrorResponse) => {
        console.error('updatePassword(): ', err.error);
        reject(err.error);
      });
    });
  }

  public async updateNotices(events: NoticeModel[]): Promise<string | void> {
    // console.log('updateNotices(): Entering');
    const token = await this.auth.getToken();

    return new Promise<string | void>((resolve, reject) => {
      this.http.post(this.apiUrl + '/notices', {events: events}, {
        headers: HttpHeadersHelper.createHeaders(token),
        withCredentials: false
      })
      .toPromise()
      .then((result: { success: boolean }) => {
        if (result.success) {
          resolve();
        }
      })
      .catch((err: HttpErrorResponse) => {
        console.error('updateNotices(): ', err.error);
        reject(err.error);
      });
    });
  }

  public async getUserInfo(): Promise<UserModel> {
    // console.log('getUserInfo(): Entering: ', this.userInfo);
    const token = await this.auth.getToken();

    return new Promise<UserModel>((resolve, reject) => {
      if (this.userInfo === undefined || this.userInfo === null) {
        this.http.get(this.apiUrl + '/user', {
          headers: HttpHeadersHelper.createHeaders(token),
          withCredentials: false
        })
        .toPromise()
        .then(
          (data: {success: UserModel}) => {
            // console.log('getUserInfo(): ', data.success);
            this.userInfo = data.success;
            resolve(this.userInfo);
          }
        ).catch(reject);
      } else {
        resolve(this.userInfo);
      }
    });
  }

  public clear() {
    // console.log('clear(): User info cleared');
    this.userInfo = null;
    this.affiliates = [];
    this.rights = [];
  }

  public async findUserByMail(email: string): Promise<UserModel> {
    // console.log('findUserByMail(): Entering');
    const token = await this.auth.getToken();

    return new Promise<UserModel>((resolve, reject) => {
      this.http.get(this.apiUrl + '/find', {
        headers: HttpHeadersHelper.createHeaders(token),
        params: {email: email},
        withCredentials: false
      })
      .toPromise()
      .then((data: { success: UserModel }) => {
        console.log('findUserByMail(): ', data.success);
        resolve(data.success);
      })
      .catch((err: HttpErrorResponse) => {
        console.error('findUserByMail(): ', err.error);
        reject(err.error);
      });
    });
  }

  // Affiliated accounts functions
  // --------------------------------------------------------------------------
  public async getAffiliatedAccounts(): Promise<UserModel[]> {
    // console.log('getAffiliatedAccounts(): Entering');
    const token = await this.auth.getToken();

    return new Promise<UserModel[]>((resolve, reject) => {
      if (!this.affiliates.length) {
        this.http.get(this.apiUrl + '/subsidiary/accounts', {
          headers: HttpHeadersHelper.createHeaders(token),
          withCredentials: false
        })
        .toPromise()
        .then((data: {success: UserModel[]}) => {
          // console.log('getAffiliatedAccounts(): ', data.success);
          this.affiliates = data.success;
          resolve(data.success);
        })
        .catch((err: HttpErrorResponse) => {
          console.error('getAffiliatedAccounts(): ', err);
          reject(err);
        });
      } else {
        resolve(this.affiliates);
      }
    });
  }

  public async getAffiliatedAccountsAsArray() {
    // console.log('getAffiliatedAccountsAsArray(): Entering');
    if (!this.affiliates.length) {
      // console.log('getAffiliatedAccountsAsArray(): Before Promise');
      await this.getAffiliatedAccounts();
      // console.log('getAffiliatedAccountsAsArray(): After Promise');
    }
    return this.affiliates;
  }

  public async createNewAffiliatedAccount(user: UserModel): Promise<number> {
    // console.log('createNewAffiliatedAccount(): Entering');
    const token = await this.auth.getToken();

    return new Promise<number>((resolve, reject) => {
      this.http.post(this.apiUrl + '/subsidiary/account', user, {
        headers: HttpHeadersHelper.createHeaders(token),
        withCredentials: false
      })
      .toPromise()
      .then((result: {success: boolean, subsidiary_id: number}) => {
        // console.log('createNewAffiliatedAccount(): Success: ', result);
        user.id = result.subsidiary_id;
        this.affiliates.push(user);
        resolve(result.subsidiary_id);
      })
      .catch((err: HttpErrorResponse) => {
        console.error('createNewAffiliatedAccount(): ', err);
        reject();
      });
    });
  }

  public async deleteAffiliatedAccount(id: number): Promise<string | void> {
    // console.log('deleteAffiliatedAccount(): Entering');
    const token = await this.auth.getToken();

    return new Promise<string | void>((resolve, reject) => {
      this.http.delete(this.apiUrl + '/subsidiary/account/' + id, {
        headers: HttpHeadersHelper.createHeaders(token),
        withCredentials: false
      })
      .toPromise()
      .then(() => {
        // console.log('deleteAffiliatedAccount(): Success');
        const account = this.affiliates.find(acnt => acnt.id === id);
        this.affiliates.splice(this.affiliates.indexOf(account), 1);
        resolve();
      })
      .catch((err: HttpErrorResponse) => {
        console.error('deleteAffiliatedAccount(): ', err);
        reject(err);
      });
    });
  }

  public getAffiliatedAccountById(id: number) {
    return this.affiliates.find(account => account.id === id);
  }

  public async updateAffiliatedAccount(id: number, password: string): Promise<string | void> {
    // console.log('updateAffiliatedAccount(): Entering');
    const token = await this.auth.getToken();

    return new Promise<string | void>((resolve, reject) => {
      this.http.post(this.apiUrl + '/subsidiary/account/' + id, {password: password}, {
        headers: HttpHeadersHelper.createHeaders(token),
        withCredentials: false
      })
      .toPromise()
      .then((result: {success: boolean, pass: string}) => {
        // console.log('updateAffiliatedAccount(): Success');
        this.getAffiliatedAccountById(id).password_visible = result.pass;
        resolve(result.pass);
      })
      .catch((err: HttpErrorResponse) => {
        console.error('updateAffiliatedAccount(): ', err);
        reject(err);
      });
    });
  }

  // User rights functions
  // --------------------------------------------------------------------------
  public async getAffiliatedRights(): Promise<RightsModel[]> {
    // console.log('getAffiliatedRights(): Entering');
    const token = await this.auth.getToken();

    return new Promise<RightsModel[]>((resolve, reject) => {
      if (!this.rights.length) {
        this.http.get(this.apiUrl + '/right', {
          headers: HttpHeadersHelper.createHeaders(token),
          withCredentials: false
        })
        .toPromise()
        .then((data: {success: RightsModel[]}) => {
          // console.log('getAffiliatedRights(): ', data.success);
          this.rights = data.success;
          resolve(data.success);
        })
        .catch((err: HttpErrorResponse) => {
          console.error('getAffiliatedRights(): ', err);
          reject();
        });
      } else {
        resolve(this.rights);
      }
    });
  }

  public getAffiliatedRightsById(id: number) {
    return this.rights.find(rghts => rghts.subsidiary_id === id);
  }

  public async setAffiliatedRights(rights: RightsModel): Promise<string | void> {
    // console.log('setAffiliatedRights(): Entering');
    const token = await this.auth.getToken();

    return new Promise<string | void>((resolve, reject) => {
      this.http.post(this.apiUrl + '/right', rights, {
        headers: HttpHeadersHelper.createHeaders(token),
        withCredentials: false
      })
      .toPromise()
      .then((success: boolean) => {
        // console.log('setAffiliatedRights(): Success');
        this.rights.push(rights);
        resolve();
      })
      .catch((err: HttpErrorResponse) => {
        console.error('setAffiliatedRights(): ', err);
        reject();
      });
    });
  }

  public async deleteAffiliatedRights(id: number): Promise<string | void> {
    // console.log('deleteAffiliatedRights(): Entering');
    const token = await this.auth.getToken();

    return new Promise<string | void>((resolve, reject) => {
      this.http.delete(this.apiUrl + '/right/' + id, {
        headers: HttpHeadersHelper.createHeaders(token),
        withCredentials: false
      })
      .toPromise()
      .then(() => {
        // console.log('deleteAffiliatedRights(): Success');
        const rights  = this.rights.find(rghts => rghts.subsidiary_id === id);
        this.rights.splice(this.rights.indexOf(rights), 1);
        resolve();
      })
      .catch((err: HttpErrorResponse) => {
        console.error('deleteAffiliatedRights(): ', err);
        reject(err);
      });
    });
  }

  public async sendPlayerInfo(identity: any, operation: string, deviceIds: string = ""): Promise<string | void> {
    // console.log('sendPlayerInfo(): Entering');
    const token = await this.auth.getToken();

    return new Promise<string | void>((resolve, reject) => {
      this.http.post(this.apiUrl + '/user/player',
        {
          identity:   identity,
          operation:  operation,
          deviceIds:  deviceIds
        }, {
          headers: HttpHeadersHelper.createHeaders(token),
          withCredentials: false
      })
      .toPromise()
      .then((result: { success: boolean }) => {
        if (result.success) {
          // switch (operation) {
          //   case 'save':
          //     this.playerInfo = identity;
          //     break;
          //   case 'remove':
          //     this.playerInfo = null;
          //     break;
          // }
          resolve();
        }
      })
      .catch((err: HttpErrorResponse) => {
        console.error('sendPlayerInfo(): ', err.error);
        reject(err.error.error);
      });
    });
  }

  public async getPlayerInfo(): Promise<PlayerModel[]> {
    // console.log('sendPlayerInfo(): Entering');
    const token = await this.auth.getToken();

    return new Promise<PlayerModel[]>((resolve, reject) => {
      this.http.get(this.apiUrl + '/user/player',
        {
          headers: HttpHeadersHelper.createHeaders(token),
          withCredentials: false
        })
        .toPromise()
        .then((result: { success: boolean, players: PlayerModel[]}) => {
          if (result.success) {
            resolve(result.players);
          }
        })
        .catch((err: HttpErrorResponse) => {
          console.error('getPlayerInfo(): ', err.error);
          reject(err.error.error);
        });
    });
  }

  public async switchNotificationMode(): Promise<string | void> {
    // console.log('switchNotificationMode(): Entering');
    const push = !this.userInfo.push;

    const token = await this.auth.getToken();

    return new Promise<string | void>((resolve, reject) => {
      this.http.post(this.apiUrl + '/user/push',
        {
          push: push
        }, {
          headers: HttpHeadersHelper.createHeaders(token),
          withCredentials: false
      })
      .toPromise()
      .then((result: { success: boolean }) => {
        this.userInfo.push = push;
        resolve();
      })
      .catch((err: HttpErrorResponse) => {
        console.error('switchNotificationMode(): ', err.error);
        reject(err.error.error);
      });
    });
  }
}
